// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Request CREATE / EDIT / VIEW page
// (Stage 6P.1 + 6P.2).
//
// Routes:
//   /erp/purchase/requests/new    → create (no :id)
//   /erp/purchase/requests/:id    → edit / view (status drives mode)
//
// Layout (top → bottom):
//   1. Header strip  : icon + title + PR# badge + Status tag
//   2. Sticky toolbar: Back · Save Draft · Submit · Approve · Reject ·
//                      Cancel · Convert to PO · Add Line
//   3. Header card   : PR No (readonly), PR Date, Required By,
//                      Request Type, Priority, Department, Project Ref,
//                      Customer Ref, Remarks
//   4. Lines grid    : inline-CRUD, Excel-tight 22 px rows, slate-50
//                      uppercase headers, ⋯ row-actions menu. Columns
//                      flex per item_type via normalizePurchaseLineByItemType.
//
// Money math is mirrored from purchaseHelpers ONLY for instant grid
// feedback. The backend (services/erp/purchase) remains the source
// of truth and recomputes everything on save.
//
// All AntD primitives; Tailwind utilities only for thin grid/flex
// layout aids. Page-scoped CSS lives in the co-located
// PurchaseRequestFormPage.css — no global edits.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Button, Card, Collapse, DatePicker, Dropdown, Form, Input, InputNumber,
  Modal, Select, Space, Spin, Switch, Table, Tag, Tooltip, message
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, SaveOutlined, SendOutlined,
  CheckCircleOutlined, CloseCircleOutlined, StopOutlined,
  SwapOutlined, MoreOutlined, DeleteOutlined, FileTextOutlined,
  ReloadOutlined, SolutionOutlined
} from '@ant-design/icons';

import { purchaseRequestsApi } from './purchaseApi';
import {
  ITEM_TYPE_OPTIONS, PR_PRIORITY_OPTIONS,
  PR_REQUEST_TYPE_OPTIONS,
  normalizePurchaseRequest, normalizePurchaseLineByItemType,
  calculatePurchaseRequestLineAmount, buildPurchaseRequestPayload,
  validatePurchaseRequest, statusLabel, statusColor,
  canEditPurchaseRequest, canSubmitPurchaseRequest,
  canApprovePurchaseRequest, canRejectPurchaseRequest,
  canCancelPurchaseRequest, canConvertPurchaseRequest,
  canDeletePurchaseRequest
} from './purchaseHelpers';
import ItemMasterSelect from './ItemMasterSelect';
import SupplierMasterSelect from './SupplierMasterSelect';
import { uomApi } from '../masters/uoms/uomApi';
import { rfqApi } from '../rfq/rfqApi';
import { supplierApi } from '../masters/suppliers/supplierApi';
import './PurchaseRequestFormPage.css';

// Item-type families that show length-driven fields.
const LENGTH_TYPES = new Set(['PROFILE', 'UPVC', 'ALUMINIUM']);
const GLASS_TYPE   = 'GLASS';

// Standard CBIC GST slabs for the convert-time default.
const GST_RATE_OPTIONS = Object.freeze(
  [0, 0.1, 0.25, 1, 3, 5, 12, 18, 28].map((v) => ({ value: v, label: `${v}%` }))
);

// Item-type families exempt from the Item Master lock (free-text allowed).
// Everything else must be picked from the Item Master. Mirrors the backend
// MASTER_OPTIONAL_ITEM_TYPES in services/erp/purchase/itemMasterGuard.js.
const MASTER_OPTIONAL_TYPES = new Set(['SERVICE', 'OTHER']);

// Fallback UOM lists — used only if the UOM Master lookup is unavailable, so
// the grid never ships with an empty unit dropdown.
const FALLBACK_UOM_OPTIONS = Object.freeze(
  ['PCS', 'NOS', 'KG', 'GM', 'LTR', 'MTR', 'RFT', 'RMT', 'SQMT', 'SQFT',
   'SET', 'BOX', 'PAIR', 'HRS', 'JOB'].map((v) => ({ value: v, label: v }))
);
const FALLBACK_LEN_UOM_OPTIONS = Object.freeze(
  ['RFT', 'RMT', 'MTR'].map((v) => ({ value: v, label: v }))
);

// Stable client row id generator for brand-new lines.
let _newLineCounter = 0;
function newClientRowId() {
  _newLineCounter += 1;
  return `pr-new-${Date.now()}-${_newLineCounter}`;
}

// Empty line factory — see Stage 6P.1 spec: default OTHER / qty 1 / PCS.
function makeEmptyLine() {
  return {
    _clientRowId:       newClientRowId(),
    _dirty:             true,
    id:                 null,
    line_no:            null,
    item_id:            null,
    item_code:          null,
    item_name:          '',
    item_type:          'OTHER',
    qty:                1,
    uom:                'PCS',
    width_mm:           0,
    height_mm:          0,
    sqmt:               0,
    sqft:               0,
    length_value:       0,
    total_length:       0,
    estimated_rate:     0,
    estimated_amount:   0,
    required_by_date:   null,
    preferred_supplier: '',
    remarks:            ''
  };
}

// Recompute derived fields after an edit. Re-runs the snake-case
// normaliser so item_type changes clear stale dimensions.
function recalcLine(line) {
  const norm = normalizePurchaseLineByItemType(line);
  const estimated_amount = calculatePurchaseRequestLineAmount(norm);
  return {
    ...line,
    ...norm,
    estimated_amount
  };
}

// ─────────────────────────────────────────────────────────────────
// Reject / Cancel / Convert modals
// ─────────────────────────────────────────────────────────────────

function ReasonModal({ open, title, okText, requireReason, busy, onOk, onCancel }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
    <Modal
      className="pr-modal"
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{
        danger: true,
        loading: busy,
        disabled: requireReason && !reason.trim()
      }}
      onOk={() => onOk(reason.trim() || null)}
      onCancel={onCancel}
      destroyOnClose
      width={520}>
      <Form layout="vertical">
        <Form.Item
          label={requireReason ? 'Reason (required)' : 'Reason (optional)'}
          required={!!requireReason}>
          <Input.TextArea
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Add a short note for the audit trail…"
            autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ConvertToPoModal({ open, busy, onOk, onCancel }) {
  const [form] = Form.useForm();
  // supplier_id is held outside the AntD Form (it is set by the master picker,
  // not typed). null = free-text supplier (prior behaviour preserved).
  const [supplierId, setSupplierId] = useState(null);
  const [supplierLabel, setSupplierLabel] = useState('');
  useEffect(() => {
    if (!open) { form.resetFields(); setSupplierId(null); setSupplierLabel(''); }
  }, [open, form]);

  // When a master supplier is chosen, stamp its id and auto-fill the
  // free-text identity fields so name / GSTIN / state ride through to the PO.
  const onPickSupplier = useCallback((id, sup) => {
    setSupplierId(id);
    if (sup) {
      setSupplierLabel(sup.supplierName || '');
      form.setFieldsValue({
        supplier_name:       sup.supplierName || '',
        supplier_gstin:      sup.supplierGstin || '',
        supplier_state_code: sup.supplierStateCode || ''
      });
    }
  }, [form]);
  const onClearSupplier = useCallback(() => {
    setSupplierId(null);
    setSupplierLabel('');
  }, []);

  const submit = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    onOk({
      supplier_id:          supplierId || null,
      supplier_name:        (values.supplier_name || '').trim(),
      supplier_gstin:       (values.supplier_gstin || '').trim().toUpperCase() || null,
      supplier_state_code:  (values.supplier_state_code || '').trim().toUpperCase() || null,
      company_state_code:   (values.company_state_code  || '').trim().toUpperCase() || null,
      default_gst_rate:     values.default_gst_rate == null ? 0 : Number(values.default_gst_rate),
      expected_delivery_date:
        values.expected_delivery_date
          ? values.expected_delivery_date.format('YYYY-MM-DD')
          : null,
      payment_terms:        (values.payment_terms  || '').trim() || null,
      delivery_terms:       (values.delivery_terms || '').trim() || null,
      freight_terms:        (values.freight_terms  || '').trim() || null
    });
  }, [form, onOk, supplierId]);
  return (
    <Modal
      className="pr-modal"
      open={open}
      title="Convert to Purchase Order"
      okText="Convert"
      okButtonProps={{ type: 'primary', loading: busy }}
      onOk={submit}
      onCancel={onCancel}
      destroyOnClose
      width={620}>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Supplier (from master)"
          tooltip="Pick a master supplier to link payables (supplier_id rides through to the bill). Leave blank to use a free-text name only.">
          <SupplierMasterSelect
            value={supplierId || undefined}
            label={supplierLabel}
            onSelectSupplier={onPickSupplier}
            onClear={onClearSupplier}
          />
        </Form.Item>
        <Form.Item
          label="Supplier name"
          name="supplier_name"
          rules={[{ required: true, message: 'Supplier name is required.' }]}>
          <Input maxLength={200} placeholder="e.g., Acme Glass Pvt Ltd" autoFocus />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item label="Supplier GSTIN" name="supplier_gstin"
            rules={[{ max: 15, message: 'GSTIN is at most 15 characters.' }]}>
            <Input maxLength={15} placeholder="15-char GSTIN" />
          </Form.Item>
          <Form.Item
            label="Default GST rate"
            name="default_gst_rate"
            initialValue={18}
            tooltip="Applied to every converted PO line (PR lines carry no GST). Edit any line on the DRAFT PO afterwards.">
            <Select options={GST_RATE_OPTIONS} placeholder="GST %" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item
            label="Supplier state code"
            name="supplier_state_code"
            rules={[
              { len: 2, message: 'State code must be exactly 2 characters.' }
            ]}>
            <Input maxLength={2} placeholder="e.g., 27" />
          </Form.Item>
          <Form.Item
            label="Company state code"
            name="company_state_code"
            rules={[
              { len: 2, message: 'State code must be exactly 2 characters.' }
            ]}>
            <Input maxLength={2} placeholder="e.g., 27" />
          </Form.Item>
        </div>
        <Form.Item label="Expected delivery date" name="expected_delivery_date">
          <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item label="Payment terms" name="payment_terms">
            <Input maxLength={200} placeholder="e.g., Net 30" />
          </Form.Item>
          <Form.Item label="Delivery terms" name="delivery_terms">
            <Input maxLength={200} placeholder="e.g., Ex-works" />
          </Form.Item>
        </div>
        <Form.Item label="Freight terms" name="freight_terms">
          <Input maxLength={200} placeholder="e.g., To-pay" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create-RFQ modal — raise an RFQ from this approved PR, pick vendors.
// ─────────────────────────────────────────────────────────────────
function CreateRfqModal({ open, prId, onCreated, onCancel }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [picked, setPicked] = useState([]);
  useEffect(() => { if (!open) { form.resetFields(); setPicked([]); } }, [open, form]);

  const search = useCallback(async (q) => {
    try {
      const res = await supplierApi.list({
        search: q || '', page: 1, pageSize: 20,
        filters: [{ field: 'status', value: 'active', op: 'equals' }]
      });
      const rows = Array.isArray(res) ? res : (res.rows || []);
      setSupplierOpts(rows.map((s) => ({
        value: s.id,
        label: `${s.supplierCode || ''} — ${s.supplierName || ''}${s.email ? ` · ${s.email}` : ''}`
      })));
    } catch (_e) { setSupplierOpts([]); }
  }, []);

  const submit = useCallback(async () => {
    let v; try { v = await form.validateFields(); } catch (_e) { return; }
    setBusy(true);
    try {
      const rfq = await rfqApi.createFromPr(prId, {
        submission_due_date: v.submission_due_date ? v.submission_due_date.format('YYYY-MM-DD') : null,
        contact_person:  (v.contact_person || '').trim() || null,
        payment_terms:   (v.payment_terms  || '').trim() || null,
        delivery_terms:  (v.delivery_terms || '').trim() || null,
        freight_terms:   (v.freight_terms  || '').trim() || null,
        supplier_ids:    picked
      });
      message.success(`RFQ ${rfq.rfqNo} created.`);
      onCreated(rfq);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to create RFQ.');
    } finally { setBusy(false); }
  }, [form, prId, picked, onCreated]);

  return (
    <Modal className="pr-modal" open={open} title="Create Request for Quotation"
      okText="Create RFQ" okButtonProps={{ type: 'primary', loading: busy }}
      onOk={submit} onCancel={onCancel} destroyOnClose width={620}>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="Vendors" tooltip="Active suppliers — the RFQ is emailed to each vendor's address on file. You can also add vendors later.">
          <Select mode="multiple" style={{ width: '100%' }} placeholder="Search suppliers…"
            filterOption={false} onSearch={search} onFocus={() => search('')}
            options={supplierOpts} value={picked} onChange={setPicked} />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item label="Submission due date" name="submission_due_date">
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item label="Contact person" name="contact_person">
            <Input maxLength={120} placeholder="e.g., Mr. Suresh Kumar" />
          </Form.Item>
        </div>
        <Form.Item label="Payment terms" name="payment_terms">
          <Input maxLength={500} placeholder="e.g., Net 30" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item label="Delivery terms" name="delivery_terms">
            <Input maxLength={500} placeholder="e.g., Door delivery to plant" />
          </Form.Item>
          <Form.Item label="Freight terms" name="freight_terms">
            <Input maxLength={500} placeholder="e.g., Inclusive" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────

export default function PurchaseRequestFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreate = !id;

  const [pr, setPr]           = useState(() => ({
    id: null,
    prNo: null,
    prDate: dayjs().format('YYYY-MM-DD'),
    requiredByDate: null,
    requestType: 'GENERAL',
    priority: 'NORMAL',
    status: 'DRAFT',
    department: '',
    projectRef: '',
    customerRef: '',
    remarks: '',
    lines: []
  }));
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  // Commercials (est. rate / amount) are optional on a PR — pricing is
  // finalised on the PO. Hidden by default; the operator opts in.
  const [showEstimates, setShowEstimates] = useState(false);

  // UOM dropdowns are bound to the UOM Master (active units). Falls back to a
  // static list only if the lookup is unavailable.
  const [uomMaster, setUomMaster] = useState([]);
  useEffect(() => {
    let alive = true;
    uomApi.lookup({ limit: 500 })
      .then((rows) => {
        if (!alive) return;
        const list = (rows || [])
          .map((u) => ({
            value: u.uomCode || u.uom_code,
            label: u.uomCode || u.uom_code,
            title: u.uomName || u.uom_name || '',
            measurementType: u.measurementType || u.measurement_type || ''
          }))
          .filter((o) => o.value);
        setUomMaster(list);
      })
      .catch(() => { /* keep the fallback list */ });
    return () => { alive = false; };
  }, []);

  const uomSelectOptions = useMemo(
    () => (uomMaster.length ? uomMaster : FALLBACK_UOM_OPTIONS),
    [uomMaster]
  );
  const lenUomSelectOptions = useMemo(() => {
    const len = uomMaster.filter(
      (o) => String(o.measurementType || '').toUpperCase() === 'LENGTH'
    );
    return len.length ? len : FALLBACK_LEN_UOM_OPTIONS;
  }, [uomMaster]);

  // Modal state.
  const [rejectOpen, setRejectOpen]   = useState(false);
  const [cancelOpen, setCancelOpen]   = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [rfqOpen, setRfqOpen] = useState(false);

  // ── Load (edit mode only) ───────────────────────────────────
  const load = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    try {
      const raw = await purchaseRequestsApi.get(id);
      const norm = normalizePurchaseRequest(raw || {});
      // Hydrate camelCase header + ensure lines carry derived fields.
      setPr({
        id:              norm.id,
        prNo:            norm.prNo,
        prDate:          norm.prDate,
        requiredByDate:  norm.requiredByDate,
        requestType:     norm.requestType || 'GENERAL',
        priority:        norm.priority || 'NORMAL',
        status:          norm.status || 'DRAFT',
        department:      norm.department || '',
        projectRef:      norm.projectRef || '',
        customerRef:     norm.customerRef || '',
        remarks:         norm.remarks || '',
        approvedBy:      norm.approvedBy,
        approvedAt:      norm.approvedAt,
        rejectedBy:      norm.rejectedBy,
        rejectedAt:      norm.rejectedAt,
        rejectionReason: norm.rejectionReason,
        cancelledBy:     norm.cancelledBy,
        cancelledAt:     norm.cancelledAt,
        cancellationReason: norm.cancellationReason,
        convertedPoId:   norm.convertedPoId,
        convertedAt:     norm.convertedAt,
        createdAt:       norm.createdAt,
        updatedAt:       norm.updatedAt,
        lines: (norm.lines || []).map((ln) => recalcLine({
          ...ln,
          _dirty: false
        }))
      });
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load purchase request.');
    } finally {
      setLoading(false);
    }
  }, [id, isCreate]);

  useEffect(() => { load(); }, [load]);

  // ── Capability gates ────────────────────────────────────────
  const gates = useMemo(() => ({
    editable: isCreate || canEditPurchaseRequest(pr),
    canSubmit:  !isCreate && canSubmitPurchaseRequest(pr),
    canApprove: !isCreate && canApprovePurchaseRequest(pr),
    canReject:  !isCreate && canRejectPurchaseRequest(pr),
    canCancel:  !isCreate && canCancelPurchaseRequest(pr),
    canConvert: !isCreate && canConvertPurchaseRequest(pr),
    canDelete:  !isCreate && canDeletePurchaseRequest(pr)
  }), [pr, isCreate]);

  // ── Header patch helpers ────────────────────────────────────
  const setHeader = (patch) => setPr((prev) => ({ ...prev, ...patch }));

  // ── Line patch helpers ──────────────────────────────────────
  const patchLine = useCallback((clientRowId, patch) => {
    setPr((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((ln) =>
        ln._clientRowId === clientRowId
          ? recalcLine({ ...ln, ...patch, _dirty: true })
          : ln
      )
    }));
  }, []);

  const addLine = useCallback(() => {
    setPr((prev) => ({
      ...prev,
      lines: [...(prev.lines || []), recalcLine(makeEmptyLine())]
    }));
  }, []);

  const removeLine = useCallback((clientRowId) => {
    setPr((prev) => ({
      ...prev,
      lines: (prev.lines || []).filter((ln) => ln._clientRowId !== clientRowId)
    }));
  }, []);

  const duplicateLine = useCallback((clientRowId) => {
    setPr((prev) => {
      const lines = prev.lines || [];
      const idx = lines.findIndex((ln) => ln._clientRowId === clientRowId);
      if (idx < 0) return prev;
      const src = lines[idx];
      const copy = recalcLine({
        ...src,
        _clientRowId: newClientRowId(),
        _dirty: true,
        id: null,
        line_no: null
      });
      return {
        ...prev,
        lines: [...lines.slice(0, idx + 1), copy, ...lines.slice(idx + 1)]
      };
    });
  }, []);

  // ── Save / Submit / Approve / Reject / Cancel / Convert ─────

  const buildPayloadForSave = useCallback(() => buildPurchaseRequestPayload({
    prDate:         pr.prDate,
    requiredByDate: pr.requiredByDate,
    requestType:    pr.requestType,
    priority:       pr.priority,
    status:         pr.status,
    department:     pr.department,
    projectRef:     pr.projectRef,
    customerRef:    pr.customerRef,
    remarks:        pr.remarks,
    lines:          pr.lines
  }), [pr]);

  const runValidation = useCallback(() => {
    // Required By is mandatory on this form (must also be on/after the PR date).
    if (!pr.requiredByDate) {
      message.error('Required By date is mandatory.');
      return false;
    }
    if (pr.prDate && dayjs(pr.requiredByDate).isBefore(dayjs(pr.prDate), 'day')) {
      message.error('Required By date cannot be before the PR date.');
      return false;
    }
    const { ok, errors } = validatePurchaseRequest({
      prDate:         pr.prDate,
      requiredByDate: pr.requiredByDate,
      requestType:    pr.requestType,
      priority:       pr.priority,
      status:         pr.status,
      lines:          pr.lines
    });
    if (!ok) {
      message.error(errors[0]?.message || 'Please fix the highlighted errors.');
    }
    return ok;
  }, [pr]);

  const saveDraft = useCallback(async () => {
    if (!runValidation()) return;
    setSaving(true);
    try {
      const payload = buildPayloadForSave();
      const saved = isCreate
        ? await purchaseRequestsApi.create(payload)
        : await purchaseRequestsApi.update(id, payload);
      message.success(isCreate ? 'Purchase request created.' : 'Purchase request saved.');
      if (isCreate && saved && saved.id) {
        navigate(`/erp/purchase/requests/${saved.id}`, { replace: true });
      } else {
        load();
      }
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to save purchase request.');
    } finally {
      setSaving(false);
    }
  }, [buildPayloadForSave, id, isCreate, load, navigate, runValidation]);

  const submitRequest = useCallback(async () => {
    if (!id) {
      message.warning('Save the draft first before submitting.');
      return;
    }
    if (!runValidation()) return;
    setBusyAction('submit');
    try {
      await purchaseRequestsApi.submit(id);
      message.success('Purchase request submitted for approval.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to submit request.');
    } finally {
      setBusyAction(null);
    }
  }, [id, load, runValidation]);

  const approveRequest = useCallback(async () => {
    if (!id) return;
    setBusyAction('approve');
    try {
      await purchaseRequestsApi.approve(id);
      message.success('Purchase request approved.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to approve request.');
    } finally {
      setBusyAction(null);
    }
  }, [id, load]);

  const rejectRequest = useCallback(async (reason) => {
    if (!id) return;
    setBusyAction('reject');
    try {
      await purchaseRequestsApi.reject(id, { reason });
      message.success('Purchase request rejected.');
      setRejectOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to reject request.');
    } finally {
      setBusyAction(null);
    }
  }, [id, load]);

  const cancelRequest = useCallback(async (reason) => {
    if (!id) return;
    setBusyAction('cancel');
    try {
      await purchaseRequestsApi.cancel(id, { reason });
      message.success('Purchase request cancelled.');
      setCancelOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to cancel request.');
    } finally {
      setBusyAction(null);
    }
  }, [id, load]);

  const convertToPo = useCallback(async (payload) => {
    if (!id) return;
    setBusyAction('convert');
    try {
      const newPo = await purchaseRequestsApi.convertToPo(id, payload);
      message.success('Converted to Purchase Order.');
      setConvertOpen(false);
      const newId = newPo && (newPo.id || newPo.poId);
      if (newId) {
        navigate(`/erp/purchase/orders/${newId}`);
      } else {
        load();
      }
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to convert to PO.');
    } finally {
      setBusyAction(null);
    }
  }, [id, load, navigate]);

  const deleteRequest = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: 'Delete purchase request?',
      content: 'This will move the draft to the recycle bin. You can recreate it later.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await purchaseRequestsApi.remove(id);
          message.success('Purchase request deleted.');
          navigate('/erp/purchase/requests');
        } catch (e) {
          message.error(e?.response?.data?.error?.message
            || e.message || 'Failed to delete request.');
        }
      }
    });
  }, [id, navigate]);

  // ── Columns — flex per item_type ────────────────────────────

  const renderRowActions = useCallback((row) => {
    const items = [
      {
        key: 'duplicate',
        label: 'Duplicate line',
        icon: <FileTextOutlined />,
        disabled: !gates.editable,
        onClick: () => duplicateLine(row._clientRowId)
      },
      {
        key: 'remove',
        label: 'Remove line',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: !gates.editable,
        onClick: () => removeLine(row._clientRowId)
      }
    ];
    return (
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        placement="bottomRight">
        <Button
          className="pr-row-action-btn"
          size="small"
          type="text"
          icon={<MoreOutlined />} />
      </Dropdown>
    );
  }, [duplicateLine, removeLine, gates.editable]);

  const columns = useMemo(() => {
    const ro = !gates.editable;
    return [
      {
        title: '#',
        width: 44,
        align: 'center',
        className: 'pr-col-serial',
        render: (_v, _row, idx) => (
          <span className="pr-serial">{idx + 1}</span>
        )
      },
      {
        // Item + type folded into one column: pick the type, then the
        // master item (or free-text for SERVICE/OTHER). The master code
        // shows as a quiet caption — no separate columns for either.
        title: 'Item',
        dataIndex: 'item_name',
        width: 300,
        className: 'pr-col-item',
        render: (_v, row) => {
          // Tolerate snake/camel from API reload (item_name vs itemName).
          const curName = row.item_name || row.itemName || '';
          const curId   = row.item_id   || row.itemId   || null;
          const curCode = row.item_code || row.itemCode || null;
          // SERVICE / OTHER lines stay free-text (exempt from the master
          // lock); every other (product) line must be picked from the
          // Item Master so item_id is always a real, active master row.
          const isFree = MASTER_OPTIONAL_TYPES.has(row.item_type);
          return (
            <div className="pr-item-cell">
              <Select
                className="pr-item-type"
                size="small"
                variant="borderless"
                value={row.item_type || undefined}
                disabled={ro}
                options={ITEM_TYPE_OPTIONS}
                onChange={(val) => patchLine(row._clientRowId, {
                  item_type: val,
                  itemType:  val,
                  // Reset item identity when crossing the product/service
                  // boundary: products must be re-picked from the master;
                  // SERVICE/OTHER drops the master id and reverts to text.
                  item_id:   null,
                  itemId:    null,
                  item_code: null,
                  item_name: '',
                  itemName:  '',
                  width_mm:     val === GLASS_TYPE ? row.width_mm : 0,
                  height_mm:    val === GLASS_TYPE ? row.height_mm : 0,
                  length_value: LENGTH_TYPES.has(val) ? row.length_value : 0
                })} />
              {isFree ? (
                <Input
                  className="pr-item-name"
                  size="small"
                  variant="borderless"
                  value={curName}
                  placeholder="Describe the item…"
                  maxLength={500}
                  disabled={ro}
                  onChange={(e) => patchLine(row._clientRowId, {
                    item_id:   null, itemId: null, item_code: null,
                    item_name: e.target.value, itemName: e.target.value
                  })} />
              ) : (
                <ItemMasterSelect
                  value={curId || undefined}
                  label={curName}
                  disabled={ro}
                  onClear={() => patchLine(row._clientRowId, {
                    item_id: null, itemId: null, item_code: null,
                    item_name: '', itemName: ''
                  })}
                  onSelectItem={(idv, item) => patchLine(row._clientRowId, {
                    item_id:   idv,
                    itemId:    idv,
                    item_code: item ? (item.itemCode || null) : null,
                    item_name: item ? (item.itemName || curName) : curName,
                    itemName:  item ? (item.itemName || curName) : curName,
                    // Helpful defaults only when the line hasn't set them yet.
                    uom: row.uom || (item && item.defaultUomCode) || 'PCS',
                    estimated_rate: Number(row.estimated_rate) > 0
                      ? row.estimated_rate
                      : (item && Number(item.purchaseRate)) || row.estimated_rate,
                    estimatedRate: Number(row.estimated_rate) > 0
                      ? row.estimated_rate
                      : (item && Number(item.purchaseRate)) || row.estimated_rate
                  })} />
              )}
              {curCode && <div className="pr-item-code">{curCode}</div>}
            </div>
          );
        }
      },
      {
        // Single adaptive "Size" column — replaces W/H/SQMT/SQFT/Length/
        // Len UOM/Total Length. Shows only what the item type needs, plus a
        // scaled pane preview and the derived area/length. No empty dashes.
        title: 'Size',
        dataIndex: 'size',
        width: 250,
        className: 'pr-col-size',
        render: (_v, row) => {
          if (row.item_type === GLASS_TYPE) {
            const w = Number(row.width_mm) || 0;
            const h = Number(row.height_mm) || 0;
            const mx = Math.max(w, h) || 1;
            const pw = w > 0 ? Math.max(4, Math.round((w / mx) * 26)) : 0;
            const ph = h > 0 ? Math.max(4, Math.round((h / mx) * 26)) : 0;
            return (
              <div className="pr-size-cell">
                <div className="pr-size-pv">
                  {pw > 0 && ph > 0 && (
                    <span className="pr-pane" style={{ width: pw, height: ph }} />
                  )}
                </div>
                <div className="pr-size-body">
                  <div className="pr-size-dims">
                    <InputNumber size="small" variant="borderless" controls={false}
                      min={0} step={1} value={w || null} placeholder="W"
                      disabled={ro} className="pr-dim-input"
                      onChange={(n) => patchLine(row._clientRowId, {
                        width_mm: Number(n) || 0, widthMm: Number(n) || 0 })} />
                    <span className="pr-size-x">×</span>
                    <InputNumber size="small" variant="borderless" controls={false}
                      min={0} step={1} value={h || null} placeholder="H"
                      disabled={ro} className="pr-dim-input"
                      onChange={(n) => patchLine(row._clientRowId, {
                        height_mm: Number(n) || 0, heightMm: Number(n) || 0 })} />
                    <span className="pr-size-unit">mm</span>
                  </div>
                  <div className="pr-size-derived">
                    {Number(row.sqmt || 0) > 0
                      ? `${Number(row.sqmt).toFixed(2)} SQM · ${Number(row.sqft || 0).toFixed(1)} SQFT`
                      : 'enter width × height'}
                  </div>
                </div>
              </div>
            );
          }
          if (LENGTH_TYPES.has(row.item_type)) {
            return (
              <div className="pr-size-cell">
                <div className="pr-size-pv"><span className="pr-lenbar" /></div>
                <div className="pr-size-body">
                  <div className="pr-size-dims">
                    <InputNumber size="small" variant="borderless" controls={false}
                      min={0} step={0.01} value={Number(row.length_value) || null}
                      placeholder="Length" disabled={ro} className="pr-dim-input"
                      onChange={(n) => patchLine(row._clientRowId, {
                        length_value: Number(n) || 0, lengthValue: Number(n) || 0 })} />
                    <Select size="small" variant="borderless"
                      value={row.length_uom || undefined} placeholder="UOM"
                      disabled={ro} className="pr-len-uom" showSearch
                      optionFilterProp="label"
                      options={lenUomSelectOptions}
                      onChange={(val) => patchLine(row._clientRowId, {
                        length_uom: val, lengthUom: val })} />
                  </div>
                  <div className="pr-size-derived">
                    {Number(row.total_length || 0) > 0
                      ? `${Number(row.total_length).toFixed(2)} total`
                      : 'length per piece'}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <span className="pr-size-na">
              no size — counted in {row.uom || 'units'}
            </span>
          );
        }
      },
      {
        // Qty + UOM folded into one column.
        title: 'Qty',
        dataIndex: 'qty',
        width: 150,
        render: (v, row) => (
          <div className="pr-qty-cell">
            <InputNumber
              size="small" variant="borderless" controls={false}
              min={0} step={1} value={v || 0} disabled={ro}
              className="pr-qty-input"
              onChange={(n) => patchLine(row._clientRowId, { qty: Number(n) || 0 })} />
            <Select
              size="small" variant="borderless" value={row.uom || undefined}
              placeholder="UOM" disabled={ro} showSearch className="pr-qty-uom"
              optionFilterProp="label"
              options={uomSelectOptions}
              onChange={(val) => patchLine(row._clientRowId, { uom: val })} />
          </div>
        )
      },
      // Commercials are optional on a PR — only shown when "Show estimates"
      // is on. Pricing is finalised on the purchase order.
      ...(showEstimates ? [
        {
          title: 'Est. rate',
          dataIndex: 'estimated_rate',
          width: 110,
          align: 'right',
          render: (v, row) => (
            <InputNumber
              size="small" variant="borderless" controls={false}
              min={0} step={0.01} value={v || 0} disabled={ro}
              style={{ width: '100%', textAlign: 'right' }}
              onChange={(n) => patchLine(row._clientRowId, {
                estimated_rate: Number(n) || 0,
                estimatedRate:  Number(n) || 0
              })} />
          )
        },
        {
          title: 'Est. amount',
          dataIndex: 'estimated_amount',
          width: 120,
          align: 'right',
          className: 'pr-col-amt',
          render: (v) => (
            <span className="pr-cell-derived">
              {Number(v || 0).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          )
        }
      ] : []),
      {
        title: 'Remarks',
        dataIndex: 'remarks',
        width: 200,
        render: (v, row) => (
          <Input
            size="small"
            variant="borderless"
            value={v || ''}
            placeholder="Notes"
            maxLength={500}
            disabled={ro}
            onChange={(e) => patchLine(row._clientRowId, {
              remarks: e.target.value
            })} />
        )
      },
      {
        title: '',
        key: '_actions',
        width: 38,
        fixed: 'right',
        align: 'center',
        render: (_v, row) => renderRowActions(row)
      }
    ];
  }, [gates.editable, patchLine, renderRowActions, showEstimates,
      uomSelectOptions, lenUomSelectOptions]);

  // ── Header card render ──────────────────────────────────────

  const ro = !gates.editable;
  const totalEstimated = useMemo(() => (pr.lines || []).reduce(
    (acc, ln) => acc + Number(ln.estimated_amount || 0), 0
  ), [pr.lines]);

  return (
    <div className="pr-shell">
      {/* Header strip */}
      <div className="pr-headstrip">
        <div className="pr-headstrip__lead">
          <span className="pr-headstrip__icon"><FileTextOutlined /></span>
          <div className="pr-headstrip__text">
            <span className="pr-headstrip__title">
              {isCreate ? 'New Purchase Request' : 'Purchase Request'}
              {pr.prNo && (
                <span className="pr-id-badge">
                  PR <strong>{pr.prNo}</strong>
                </span>
              )}
              {pr.status && (
                <Tag color={statusColor(pr.status)} style={{ marginLeft: 6 }}>
                  {statusLabel(pr.status)}
                </Tag>
              )}
            </span>
            <span className="pr-headstrip__subtitle">
              {isCreate
                ? 'Add at least one line, then Save Draft or Submit for approval.'
                : ro
                  ? 'This request is locked — header & lines are read-only at this status.'
                  : 'Edit the header and lines, then Save Draft or Submit for approval.'}
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="pr-stat-chip">
            <strong>{(pr.lines || []).length}</strong> lines
          </span>
          {showEstimates && (
            <span className="pr-stat-chip">
              est. <strong>
                {totalEstimated.toLocaleString('en-IN', {
                  minimumFractionDigits: 2, maximumFractionDigits: 2
                })}
              </strong>
            </span>
          )}
        </Space>
      </div>

      {/* Toolbar */}
      <div className="pr-toolbar">
        <Tooltip title="Back to list">
          <Button size="small" icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/erp/purchase/requests')}>
            Back
          </Button>
        </Tooltip>
        {!isCreate && (
          <Tooltip title="Reload">
            <Button size="small" icon={<ReloadOutlined />} onClick={load} />
          </Tooltip>
        )}
        <div className="pr-toolbar__spacer" />

        <Button size="small" icon={<PlusOutlined />}
          onClick={addLine} disabled={ro}>
          Add Line
        </Button>

        <Button size="small" type="primary" icon={<SaveOutlined />}
          onClick={saveDraft} loading={saving}
          disabled={ro || saving}>
          Save Draft
        </Button>

        {gates.canSubmit && (
          <Button size="small" icon={<SendOutlined />}
            onClick={submitRequest}
            loading={busyAction === 'submit'}>
            Submit Request
          </Button>
        )}
        {gates.canApprove && (
          <Button size="small" type="primary" ghost
            icon={<CheckCircleOutlined />}
            onClick={approveRequest}
            loading={busyAction === 'approve'}>
            Approve
          </Button>
        )}
        {gates.canReject && (
          <Button size="small" danger icon={<CloseCircleOutlined />}
            onClick={() => setRejectOpen(true)}
            loading={busyAction === 'reject'}>
            Reject
          </Button>
        )}
        {gates.canCancel && (
          <Button size="small" icon={<StopOutlined />}
            onClick={() => setCancelOpen(true)}
            loading={busyAction === 'cancel'}>
            Cancel
          </Button>
        )}
        {gates.canConvert && (
          <Button size="small" icon={<SolutionOutlined />}
            onClick={() => setRfqOpen(true)}>
            Create RFQ
          </Button>
        )}
        {gates.canConvert && (
          <Button size="small" type="primary" icon={<SwapOutlined />}
            onClick={() => setConvertOpen(true)}
            loading={busyAction === 'convert'}>
            Convert to PO
          </Button>
        )}
        {gates.canDelete && (
          <Tooltip title="Delete draft">
            <Button size="small" danger icon={<DeleteOutlined />}
              onClick={deleteRequest} />
          </Tooltip>
        )}
      </div>

      {/* Status banner — shown for non-editable states with rich context */}
      {!isCreate && pr.status === 'REJECTED' && pr.rejectionReason && (
        <Alert
          className="mb-1"
          type="error"
          showIcon
          message="This purchase request was rejected"
          description={pr.rejectionReason}
          style={{ marginBottom: 6 }} />
      )}
      {!isCreate && pr.status === 'CANCELLED' && pr.cancellationReason && (
        <Alert
          className="mb-1"
          type="warning"
          showIcon
          message="This purchase request was cancelled"
          description={pr.cancellationReason}
          style={{ marginBottom: 6 }} />
      )}
      {!isCreate && pr.status === 'CONVERTED_TO_PO' && pr.convertedPoId && (
        <Alert
          className="mb-1"
          type="info"
          showIcon
          message="This purchase request was converted to a Purchase Order"
          description={
            <a onClick={() => navigate(`/erp/purchase/orders/${pr.convertedPoId}`)}>
              Open the linked Purchase Order →
            </a>
          }
          style={{ marginBottom: 6 }} />
      )}

      <Spin spinning={loading}>
        {/* Header card — the four essentials up front; optional details
            tuck behind a collapse so the required fields stand out. PR No
            and Status live in the header strip above. */}
        <Card className="pr-header-card" bordered={false}>
          <Form layout="vertical" disabled={ro}>
            <div className="grid grid-cols-12 gap-x-3">
              <div className="col-span-3">
                <Form.Item label="PR Date" required>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD-MMM-YYYY"
                    value={pr.prDate ? dayjs(pr.prDate) : null}
                    onChange={(d) => setHeader({
                      prDate: d ? d.format('YYYY-MM-DD') : null
                    })} />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Required By" required>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD-MMM-YYYY"
                    placeholder="Required — when is it needed?"
                    value={pr.requiredByDate ? dayjs(pr.requiredByDate) : null}
                    onChange={(d) => setHeader({
                      requiredByDate: d ? d.format('YYYY-MM-DD') : null
                    })} />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Request Type" required>
                  <Select
                    value={pr.requestType}
                    options={PR_REQUEST_TYPE_OPTIONS}
                    onChange={(v) => setHeader({ requestType: v })} />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Priority" required>
                  <Select
                    value={pr.priority}
                    options={PR_PRIORITY_OPTIONS}
                    onChange={(v) => setHeader({ priority: v })} />
                </Form.Item>
              </div>
            </div>

            <Collapse
              ghost
              className="pr-more"
              items={[{
                key: 'more',
                label: 'More details — department, project ref, customer ref, notes',
                children: (
                  <div className="grid grid-cols-12 gap-x-3">
                    <div className="col-span-3">
                      <Form.Item label="Department">
                        <Input
                          value={pr.department || ''}
                          maxLength={120}
                          placeholder="e.g., Production"
                          onChange={(e) => setHeader({ department: e.target.value })} />
                      </Form.Item>
                    </div>
                    <div className="col-span-3">
                      <Form.Item label="Project Ref">
                        <Input
                          value={pr.projectRef || ''}
                          maxLength={120}
                          placeholder="Optional project / job code"
                          onChange={(e) => setHeader({ projectRef: e.target.value })} />
                      </Form.Item>
                    </div>
                    <div className="col-span-3">
                      <Form.Item label="Customer Ref">
                        <Input
                          value={pr.customerRef || ''}
                          maxLength={120}
                          placeholder="Optional customer reference"
                          onChange={(e) => setHeader({ customerRef: e.target.value })} />
                      </Form.Item>
                    </div>
                    <div className="col-span-3">
                      <Form.Item label="Remarks">
                        <Input
                          value={pr.remarks || ''}
                          maxLength={500}
                          placeholder="Header notes"
                          onChange={(e) => setHeader({ remarks: e.target.value })} />
                      </Form.Item>
                    </div>
                  </div>
                )
              }]} />
          </Form>
        </Card>

        {/* Lines grid */}
        <div className="pr-grid-card">
          <div className="pr-grid-titlebar">
            <span>Items</span>
            <div className="pr-grid-actions">
              <span className="pr-est-toggle">
                <span className="pr-est-toggle__lbl">Show estimates</span>
                <Switch size="small" checked={showEstimates}
                  onChange={setShowEstimates} />
              </span>
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                onClick={addLine}
                disabled={ro}>
                Add Line
              </Button>
            </div>
          </div>
          <Table
            className="pr-grid"
            size="small"
            rowKey="_clientRowId"
            dataSource={pr.lines || []}
            columns={columns}
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'pr-dirty-row' : ''}
            locale={{
              emptyText: (pr.lines || []).length === 0
                ? (ro
                    ? 'No lines on this purchase request.'
                    : 'No lines yet. Click Add Line to start.')
                : 'No lines match the current filter.'
            }} />
        </div>
      </Spin>

      {/* Reject modal */}
      <ReasonModal
        open={rejectOpen}
        title="Reject purchase request"
        okText="Reject"
        requireReason
        busy={busyAction === 'reject'}
        onOk={rejectRequest}
        onCancel={() => setRejectOpen(false)} />

      {/* Cancel modal */}
      <ReasonModal
        open={cancelOpen}
        title="Cancel purchase request"
        okText="Cancel request"
        requireReason={false}
        busy={busyAction === 'cancel'}
        onOk={cancelRequest}
        onCancel={() => setCancelOpen(false)} />

      {/* Convert-to-PO modal */}
      <ConvertToPoModal
        open={convertOpen}
        busy={busyAction === 'convert'}
        onOk={convertToPo}
        onCancel={() => setConvertOpen(false)} />

      {/* Create-RFQ modal */}
      <CreateRfqModal
        open={rfqOpen}
        prId={id}
        onCancel={() => setRfqOpen(false)}
        onCreated={(newRfq) => { setRfqOpen(false); navigate(`/erp/rfq/${newRfq.id}`); }} />
    </div>
  );
}
